'use client'

import { DateSelection } from '@repo/ui/DateSelection'
import { Modal } from '@repo/ui/Modal'
import { formatDate } from '@repo/utils/time'
import { useEffect, useState } from 'react'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

type PostingDateOption = 'today' | 'incurred' | 'custom'

type PostingDateDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: (date: Date) => void
  // Accepts Date or ISO string; coerced to Date internally.
  periodStart?: Date | string | null
  // When provided, a "Date incurred" option is offered (disabled if isInClosedPeriod).
  // Omit for bulk selections, where claims have differing incurred dates.
  // Accepts Date or ISO string; coerced to Date internally.
  incurredDate?: Date | string | null
  isInClosedPeriod?: boolean
  isSaving?: boolean
  // Number of claims the chosen date will apply to. >1 shows the bulk messaging.
  selectedCount?: number
}

// Coerce a Date or ISO string into a valid Date, or null if absent/invalid.
const toDate = (value?: Date | string | null): Date | null => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

export const PostingDateDialog = ({
  open,
  onClose,
  onConfirm,
  periodStart = null,
  incurredDate = null,
  isInClosedPeriod = false,
  isSaving = false,
  selectedCount = 1
}: PostingDateDialogProps) => {
  const [option, setOption] = useState<PostingDateOption>('today')
  const [customDate, setCustomDate] = useState<Date | null>(null)

  // Reset to defaults each time the dialog is opened.
  useEffect(() => {
    if (open) {
      setOption('today')
      setCustomDate(null)
    }
  }, [open])

  // Coerce date props once, up front, so the rest of the component only deals with real Dates.
  const incurred = toDate(incurredDate)
  const periodStartDate = toDate(periodStart)

  const showIncurredOption = !!incurred
  const canUseIncurredDate = showIncurredOption && !isInClosedPeriod
  const incurredDateLabel = incurred ? `Date incurred (${formatDate(incurred)})` : 'Date incurred'
  const normalisedPeriodStart = periodStartDate ? startOfDay(periodStartDate) : null

  const getResolvedDate = () => {
    if (option === 'today') return new Date()
    if (option === 'incurred') return incurred
    return customDate
  }
  const resolvedDate = getResolvedDate()

  const isValid =
    resolvedDate instanceof Date &&
    !Number.isNaN(resolvedDate.getTime()) &&
    (!normalisedPeriodStart || startOfDay(resolvedDate) >= normalisedPeriodStart)

  const handleConfirm = () => {
    if (!isValid || !resolvedDate) return
    onConfirm(resolvedDate)
  }

  const isBulk = selectedCount > 1

  return (
    <Modal open={open} handleClose={onClose} title="Select a posting date" sx={{ width: '480px' }} isConfirm>
      <MUIStack spacing={4}>
        <MUITypography variant="body1" sx={{ color: '#6B778C', mb: 0 }}>
          {isBulk ? `These ${selectedCount} claims are moving to ` : 'This claim is moving to '}
          <strong>Ready to Pay</strong>. Choose the posting date to use.
        </MUITypography>

        <MUITypography variant="body1" sx={{ color: '#6B778C', mb: 0 }}>
          {normalisedPeriodStart ? `It must be on or after ${formatDate(normalisedPeriodStart)}. ` : ''}
          {isBulk ? 'It will be applied to all selected claims.' : ''}
        </MUITypography>

        <MUIRadioGroup value={option} onChange={(e) => setOption(e.target.value as PostingDateOption)}>
          <MUIFormControlLabel value="today" control={<MUIRadio />} label={`Today (${formatDate(new Date())})`} />
          {showIncurredOption && (
            <MUITooltip
              arrow
              placement="right"
              title={canUseIncurredDate ? '' : 'The date incurred falls in a closed period and cannot be used.'}
            >
              <span>
                <MUIFormControlLabel
                  value="incurred"
                  control={<MUIRadio />}
                  disabled={!canUseIncurredDate}
                  label={incurredDateLabel}
                />
              </span>
            </MUITooltip>
          )}
          <MUIFormControlLabel value="custom" control={<MUIRadio />} label="Choose another date" />
        </MUIRadioGroup>

        {option === 'custom' && (
          <MUIBox sx={{ maxWidth: '260px' }}>
            <DateSelection
              label="Posting date"
              value={customDate}
              onChange={(date: any) => setCustomDate(date)}
              minValue={normalisedPeriodStart}
              showAdornment
              required
            />
            {customDate && !isValid && normalisedPeriodStart && (
              <MUITypography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 2 }}>
                The posting date must be on or after {formatDate(normalisedPeriodStart)}.
              </MUITypography>
            )}
          </MUIBox>
        )}

        <MUIStack direction="row" justifyContent="flex-end" spacing={2}>
          <MUIButton variant="text" onClick={onClose} disabled={isSaving}>
            Cancel
          </MUIButton>
          <MUIButton variant="contained" onClick={handleConfirm} disabled={!isValid || isSaving}>
            {isSaving ? 'Saving...' : 'Confirm'}
          </MUIButton>
        </MUIStack>
      </MUIStack>
    </Modal>
  )
}
